// 设备区域着色管理器 —— 独立于 Volume 基类，不修改 Volume 任何字段。
//
// 场景动机：电力场景里设备(变压器/刀闸/阀塔)都用 Volume 包围盒标注，平时只显示
// 点云/高斯，包围盒面隐藏(box.visible=false)。选中/告警时需要把"盒内"的点云和
// 高斯点染成指定颜色、并可闪烁，用来提示定位设备。
//
// 设计：用 Map<volume.uuid, entry> 维护"当前高亮中"的设备条目，Volume 本身不加字段。
//   - highlight()/unhighlight()/toggle() 增删条目(激活/取消着色)。
//   - 每帧 update()：收集所有高亮条目 → 算出闪烁强度 intensity → 推给
//       · 点云：pointcloud.material.setColorBoxes(entries)   (shader doColorize)
//       · 高斯：viewer._splatClipBridge.setColorBoxes(entries) (SplatEdit set_rgb)
//   - 默认无条目时推空数组 → 点云 num_colorboxes=0(着色函数被 define 剪掉，零开销)、
//     高斯 editColor.visible=false。
//
// 着色管线与裁剪(clip)管线完全正交：独立的 uniform 数组 + 独立的 shader 循环 +
// 独立的 SplatEdit，互不影响。盒内外的判定沿用 clip 盒约定：
//   colorBoxes[i] = box.matrixWorld 的逆(世界→盒本地)，盒本地 [-0.5,0.5]³ 为盒内。

import * as THREE from "../../libs/three.js/build/three.module.js";

export class DeviceRegionManager {

	constructor(viewer) {
		this.viewer = viewer;

		// key: volume.uuid  val: { volume, color:THREE.Color, flash:bool, speed:number }
		this.regions = new Map();

		this._raycaster = new THREE.Raycaster();
		this._time = 0;

		// 同时高亮着色盒数量上限。每盒占用点云 shader 的 mat4(16)+vec3(3)+float(1)
		// uniform，过多会超出 GPU uniform 上限或拖累性能。超限时截断并告警。
		this.maxColorBoxes = 100;
		this._overLimitWarned = false;

		// 复用的输出条目数组，避免每帧新建。
		this._entries = [];
	}

	// 激活某设备的着色。
	// opts: { color:(THREE.Color|number|string), flash:bool, speed:number(Hz) }
	highlight(volume, opts = {}) {
		if (!volume) return;

		let color;
		if (opts.color instanceof THREE.Color) {
			color = opts.color.clone();
		} else if (opts.color !== undefined && opts.color !== null) {
			color = new THREE.Color(opts.color);
		} else {
			color = new THREE.Color(0xff3030);
		}

		this.regions.set(volume.uuid, {
			volume,
			color,
			flash: opts.flash === true,
			speed: (typeof opts.speed === "number" && opts.speed > 0) ? opts.speed : 1.0,
		});
	}

	isHighlighted(volume) {
		return volume ? this.regions.has(volume.uuid) : false;
	}

	unhighlight(volume) {
		if (!volume) return;
		this.regions.delete(volume.uuid);
	}

	// 未高亮则按 opts 高亮；已高亮则取消。
	toggle(volume, opts = {}) {
		if (this.isHighlighted(volume)) {
			this.unhighlight(volume);
			return false;
		}
		this.highlight(volume, opts);
		return true;
	}

	clearAll() {
		this.regions.clear();
	}

	// 射线拾取设备包围盒。box.raycast 忽略 .visible，所以盒面隐藏也能命中。
	//   mouse: {x,y} 归一化设备坐标 [-1,1]
	//   candidates: 可选候选 Volume 数组，默认 viewer.scene.volumes 全体
	// 返回命中最近的 Volume 或 null。
	pickDevice(mouse, camera, candidates) {
		const cam = camera || this.viewer.scene.getActiveCamera();
		this._raycaster.setFromCamera(mouse, cam);

		const vols = candidates || this.viewer.scene.volumes;
		let best = null;
		let bestDist = Infinity;

		for (const v of vols) {
			if (!v || !v.box) continue;
			const intersects = [];
			v.box.raycast(this._raycaster, intersects);
			if (intersects.length > 0 && intersects[0].distance < bestDist) {
				bestDist = intersects[0].distance;
				best = v;
			}
		}
		return best;
	}

	// 每帧调用(viewer.update 内)。visiblePointClouds 为可见点云，dt 为帧间隔(秒)。
	update(visiblePointClouds, dt) {
		this._time += (dt || 0);

		const entries = this._entries;
		entries.length = 0;

		if (this.regions.size > 0) {
			for (const region of this.regions.values()) {
				const v = region.volume;
				if (!v || !v.box) continue;

				v.box.updateMatrixWorld();
				if (v.box.matrixWorld.determinant() === 0) continue;

				const intensity = region.flash
					? Math.abs(Math.sin(this._time * region.speed * Math.PI * 2.0))
					: 1.0;

				const matrixWorld = v.box.matrixWorld.clone();
				entries.push({
					matrixWorld,                        // 高斯桥接用(分解出 pos/quat/scale)
					inverse: matrixWorld.clone().invert(), // 点云 shader 用(世界→盒本地)
					color: region.color,
					intensity,
				});
			}
		}

		// 数量超限：截断到上限，并在跨越阈值时告警一次(不逐帧刷屏)，方便开发排查。
		if (entries.length > this.maxColorBoxes) {
			if (!this._overLimitWarned) {
				console.warn(
					`[DeviceRegionManager] 高亮着色盒数量 ${entries.length} 超过上限 ${this.maxColorBoxes}，` +
					`超出部分已忽略(只取前 ${this.maxColorBoxes} 个)。每个着色盒占用点云 shader 的 ` +
					`mat4+vec3+float uniform，过多会超出 GPU uniform 上限或影响性能，请减少同时高亮的设备数量。`
				);
				this._overLimitWarned = true;
			}
			entries.length = this.maxColorBoxes;
		} else if (this._overLimitWarned) {
			console.log(
				`[DeviceRegionManager] 高亮着色盒数量已恢复正常 (${entries.length}/${this.maxColorBoxes})。`
			);
			this._overLimitWarned = false;
		}

		// 点云：所有可见点云都推同一份条目(shader 里点在哪个盒内就染哪个色)。
		for (const pointcloud of (visiblePointClouds || [])) {
			const material = pointcloud && pointcloud.material;
			if (material && typeof material.setColorBoxes === "function") {
				material.setColorBoxes(entries);
			}
		}

		// 高斯：桥接存在时转发(spark 未加载则无桥接，纯 no-op)。
		const bridge = this.viewer && this.viewer._splatClipBridge;
		if (bridge && typeof bridge.setColorBoxes === "function") {
			bridge.setColorBoxes(entries);
		}
	}

	// 释放：清空条目并推空数组，让点云/高斯恢复原色。
	dispose() {
		this.clearAll();
		this.update(this.viewer && this.viewer.scene ? this.viewer.scene.pointclouds : [], 0);
	}
}
